import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
  HttpParams,
} from '@angular/common/http';
import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import {
  BehaviorSubject,
  catchError,
  Observable,
  finalize,
  of,
  throwError,
} from 'rxjs';
import { environment } from 'src/environments/environment';
import { AppState, ILogin, IToken } from '../shared/interfaces/utils.interface';
import { logoutSuccess } from '../_store/auth/auth.action';
import { setUserInfo } from '../_store/user/user.actions';
import { ILineLoginPayload } from '../_store/auth/auth.action';
import {
  LINE_LINK_PENDING_KEY,
  LINE_LINK_RETURN_URL_KEY,
  LineLiffService,
} from './line-liff.service';

export interface LogoutOptions {
  returnToFreeFlowLogin?: boolean;
}

interface LogoutContext {
  isLineSession: boolean;
  isInLineClient: boolean;
  returnToFreeFlowLogin: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private apiUrl = environment.apiUrl; // Replace with your actual API URL
  private refreshTokenSubject: BehaviorSubject<string | null> =
    new BehaviorSubject<string | null>(null);
  private localSessionCleanupStarted = false;
  token!: IToken;
  constructor(
    private http: HttpClient,
    private route: Router,
    private store: Store<AppState>,
    private lineLiffService: LineLiffService,
    @Inject(DOCUMENT) private document: Document,
  ) {}

  login(login: ILogin) {
    this.localSessionCleanupStarted = false;
    return this.http.post<any>(`${this.apiUrl}auth/signin`, login).pipe(
      // tap((user) => {
      //   // store user details and jwt token in local storage
      //   const { data } = user;
      //   localStorage.setItem('auth', JSON.stringify(data));
      // }),
      // tap(() => this.googleAuthService.restoreSession()),
      catchError(this.handleError),
    );
  }

  lineLogin(payload: ILineLoginPayload) {
    this.localSessionCleanupStarted = false;
    return this.http
      .post<any>(`${this.apiUrl}auth/line/signin`, payload)
      .pipe(catchError(this.handleError));
  }

  linkLineAccount(idToken: string) {
    return this.http
      .post<any>(`${this.apiUrl}auth/line/link`, { idToken })
      .pipe(catchError(this.handleError));
  }

  logout(options: LogoutOptions = {}) {
    const logoutContext = this.getLogoutContext(options);

    this.http
      .get<any>(`${this.apiUrl}auth/logout`)
      .pipe(
        catchError(() => of(null)),
        finalize(() => this.clearLocalSession(logoutContext)),
      )
      .subscribe();
  }

  clearLocalSession(logoutContext = this.getLogoutContext()): void {
    if (this.localSessionCleanupStarted) {
      return;
    }
    this.localSessionCleanupStarted = true;

    const { isLineSession, isInLineClient } = logoutContext;

    if (isLineSession && !isInLineClient) {
      try {
        this.lineLiffService.logout();
      } catch {
        // Local auth cleanup must continue if LIFF logout is unavailable.
      }
    }

    this.store.dispatch(logoutSuccess());
    this.store.dispatch(setUserInfo({ user: null }));
    localStorage.removeItem('auth');
    localStorage.removeItem('profile');
    sessionStorage.removeItem(LINE_LINK_PENDING_KEY);
    sessionStorage.removeItem(LINE_LINK_RETURN_URL_KEY);

    if (logoutContext.returnToFreeFlowLogin) {
      this.document.defaultView?.location.replace(
        '/login?flow=free&signedOut=1',
      );
      return;
    }

    if (isLineSession && isInLineClient) {
      try {
        this.lineLiffService.closeWindow();
      } catch {
        // The Yarrow session is already cleared even if LINE cannot close.
      }
      return;
    }

    if (isLineSession) {
      this.document.defaultView?.location.replace('/login');
      return;
    }

    void this.route.navigateByUrl('/login', { replaceUrl: true });
  }

  private getLogoutContext(options: LogoutOptions = {}): LogoutContext {
    let isLineSession = false;
    try {
      const storedSession = localStorage.getItem('auth');
      isLineSession =
        !!storedSession && JSON.parse(storedSession)?.authSource === 'LINE';
    } catch {
      // A malformed stored session must not prevent local cleanup.
    }

    let isInLineClient = false;
    if (isLineSession) {
      try {
        isInLineClient = this.lineLiffService.isInClient();
      } catch {
        // Treat unavailable LIFF client detection as an external browser.
      }
    }

    return {
      isLineSession,
      isInLineClient,
      returnToFreeFlowLogin: options.returnToFreeFlowLogin === true,
    };
  }

  forgotPassword(body: { email: string }) {
    return this.http
      .post<any>(`${this.apiUrl}auth/forgotPassword`, body)
      .pipe(catchError(this.handleError));
  }
  resetPassword(body: { password: string }, token: string) {
    let params = new HttpParams();
    params = params.set('token', token);
    const requestOptions = {
      params: params,
    };
    return this.http
      .post<any>(`${this.apiUrl}auth/reset/password`, body, requestOptions)
      .pipe(catchError(this.handleError));
  }

  // Function to notify subscribers when the refresh token is updated
  public notifyRefreshTokenUpdated(token: string | null): void {
    this.refreshTokenSubject.next(token);
  }

  // Getter for the observable to subscribe to refresh token updates
  public get refreshTokenObservable(): Observable<any> {
    return this.refreshTokenSubject.asObservable();
  }

  RefreshToken(): Observable<any> {
    const currentUser = localStorage.getItem('auth');
    let refreshToken;
    if (currentUser) {
      const parseData = JSON.parse(currentUser);
      refreshToken = parseData.refreshToken ? parseData.refreshToken : null;
    } else {
      refreshToken = null;
    }

    let header = new HttpHeaders({
      Authorization: 'Bearer ' + refreshToken,
    });
    return this.http.get<any>(`${this.apiUrl}auth/refresh`, {
      headers: header,
    });
  }

  private handleError(error: HttpErrorResponse) {
    if (error.error instanceof ErrorEvent) {
      // A client-side or network error occurred. Handle it accordingly.
      console.error('An error occurred:', error.error);
    } else {
      console.error('service::error ', error.error);
    }

    return throwError(() => error.error);
  }
}
